import { useId, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  combinarDiaEHora,
  formatarDataHoraPtBr,
  horaLocalDe,
  inicioDoDiaLocal,
} from "./seletor-data-hora.helpers";

type Props = {
  readonly value?: Date | undefined;
  readonly onChange: (proxima: Date | undefined) => void;
  readonly rotulo?: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  /** Quando true, datas anteriores a hoje ficam desabilitadas. */
  readonly bloquearPassado?: boolean;
  readonly className?: string;
  readonly id?: string;
};

/**
 * Seletor de data e hora no padrao Next-Elite: Popover + Calendar + hora.
 * Nao use `input type="datetime-local"`.
 */
export function SeletorDataHora({
  value,
  onChange,
  rotulo = "Data e hora",
  placeholder = "Escolher data e hora",
  disabled = false,
  bloquearPassado = false,
  className,
  id: idProp,
}: Props) {
  const idGerado = useId();
  const id = idProp ?? idGerado;
  const idHora = `${id}-hora`;
  const [aberto, setAberto] = useState(false);
  const hora = value ? horaLocalDe(value) : "09:00";

  function aoEscolherDia(dia: Date | undefined) {
    if (!dia) {
      onChange(undefined);
      return;
    }
    onChange(combinarDiaEHora(dia, hora));
  }

  function aoMudarHora(proximaHora: string) {
    if (!proximaHora) return;
    const base = value ?? new Date();
    onChange(combinarDiaEHora(base, proximaHora));
  }

  return (
    <div className={cn("grid gap-1.5", className)}>
      <Label htmlFor={id}>{rotulo}</Label>
      <Popover open={aberto} onOpenChange={setAberto}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            aria-label={rotulo}
            className={cn(
              "h-11 w-full justify-start gap-2 px-3.5 text-left text-sm font-normal",
              !value && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate">
              {value ? formatarDataHoraPtBr(value) : placeholder}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="z-[600] w-auto bg-popover p-0"
          align="start"
        >
          <Calendar
            mode="single"
            selected={value}
            onSelect={aoEscolherDia}
            disabled={
              bloquearPassado
                ? { before: inicioDoDiaLocal() }
                : undefined
            }
            initialFocus
          />
          <div className="border-t border-border px-3 py-3">
            <Label htmlFor={idHora} className="text-xs text-muted-foreground">
              Horário
            </Label>
            <Input
              id={idHora}
              type="time"
              value={hora}
              onChange={(e) => aoMudarHora(e.target.value)}
              className="mt-1.5 h-10 cursor-pointer"
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
